import React, { useEffect, useState } from "react";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import { getRoleById, updateRolePermissions } from "../../services/ApiService";
import { Button, Checkbox, Table, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";

const MODULE_LABELS = {
  client_list: "Client List",
  customer_list: "Customer List",
  directory: "Directory",
  activity_log: "Activity Log",
  fields: "Fields",
  country_list: "Country List",
  users: "Users",
  roles: "Roles",
};

const ACTION_LABELS = {
  show: "Show",
  create: "Create",
  update: "Update",
  delete: "Delete",
};

const EditRolePermissions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get("id");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [permissions, setPermissions] = useState({}); // { module: [actions] }

  useEffect(() => {
    const fetchRole = async () => {
      if (!roleId) {
        message.error("No role selected");
        navigate("/roles");
        return;
      }
      setLoading(true);
      try {
        const res = await getRoleById(roleId);
        if (res.responseCode === 200) {
          setRoleName(res.data.role_name);
          setModules(res.modules || []);
          setActions(res.actions || []);
          setPermissions(res.data.permissions || {});
        } else {
          message.error(res.responseMessage || "Failed to fetch role");
          navigate("/roles");
        }
      } catch (error) {
        console.error("Error fetching role:", error);
        message.error("Failed to fetch role");
      } finally {
        setLoading(false);
      }
    };
    fetchRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const isChecked = (module, action) =>
    (permissions[module] || []).includes(action);

  const toggleAction = (module, action, checked) => {
    setPermissions((prev) => {
      const current = new Set(prev[module] || []);
      if (checked) {
        current.add(action);
        // Show is a convenience default -- auto-check it alongside any other
        // action so an admin doesn't accidentally grant edit rights without
        // view rights. It can still be unchecked independently afterward.
        if (action !== "show") current.add("show");
      } else {
        current.delete(action);
      }
      return { ...prev, [module]: Array.from(current) };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateRolePermissions({ role_id: roleId, permissions });
      if (res.responseCode === 200) {
        message.success("Permissions updated successfully");
        setPermissions(res.data.permissions || permissions);
      } else {
        message.error(res.responseMessage || "Failed to update permissions");
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      message.error("Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      render: (module) => MODULE_LABELS[module] || module,
    },
    ...actions.map((action) => ({
      title: ACTION_LABELS[action] || action,
      key: action,
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={isChecked(record.module, action)}
          onChange={(e) => toggleAction(record.module, action, e.target.checked)}
        />
      ),
    })),
  ];

  const dataSource = modules.map((module) => ({ key: module, module }));

  return (
    <div className="main-box">
      <Sidebar activeId={11} />
      <main className="main-content">
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header title={`Permissions -- ${roleName || ""}`} />
          <div
            style={{
              flex: 1,
              minHeight: 0,
              padding: "1.5rem",
              boxSizing: "border-box",
              overflowY: "auto",
            }}
          >
            <Table
              columns={columns}
              dataSource={dataSource}
              loading={loading}
              pagination={false}
              style={{
                fontSize: "0.8rem",
                borderRadius: "1.5rem",
                background: "#FFF",
                boxShadow: "0px 1.72px 5.161px 0px rgba(0, 0, 0, 0.25)",
              }}
            />
            <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.75rem" }}>
              <Button onClick={() => navigate("/roles")}>Back</Button>
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save Permissions
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditRolePermissions;
